import { Component } from '@angular/core';
import { ClientApplication } from './models/client-application';
import { HttpClient } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { ClientApplicationSecretResponse } from './models/client-application-secret';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { IconFieldModule } from 'primeng/iconfield';
import { ButtonModule } from 'primeng/button';
import { PanelModule } from 'primeng/panel';
import { BrowserModule } from '@angular/platform-browser';

@Component({
  selector: 'app-admin-client-application',
  imports: [TableModule, DialogModule, CommonModule,InputTextModule,
      FormsModule, IconFieldModule, ButtonModule, PanelModule],
  templateUrl: './admin-client-application.component.html',
  styleUrl: './admin-client-application.component.css'
})
export class AdminClientApplicationComponent {

 // Liste aller Clients, die ein (gehashtes) Secret besitzen
 clients: ClientApplication[] = [];
  authServerUrl = '';
 // Control-Variablen für die Dialoge
 displayCreateDialog = false;
 displayEditDialog   = false;

 // Modell für "Neuen Client anlegen"
 newClient: { clientApplicationId: string; displayName: string; systemId: number | null } = {
  clientApplicationId:    '',
   displayName: '',
   systemId:    null
 };

 // Modell für "Client bearbeiten"
 editClient: ClientApplication = { clientApplicationId: '', displayName: '', systemId: 0 };
 updatedDisplayName = '';
 updatedSystemId = 0 ;

 constructor(
   private http: HttpClient,
   private messageService: MessageService,
   private oidcSecurityService: OidcSecurityService
 ) {}

 ngOnInit(): void {
  this.oidcSecurityService.getConfiguration().subscribe(config => this.authServerUrl = config?.authority ?? "");
   this.loadClients();
 }

 /**
  * Lädt alle Clients vom Backend und filtert nur jene, die ein Secret haben.
  */
 loadClients(): void {
   this.http
     .get<ClientApplication[]>(this.authServerUrl + '/client-applications')
     .subscribe({
       next: data => this.clients = data,
       error: () =>
         this.messageService.add({
           severity: 'error',
           summary: 'Fehler',
           detail: 'Konnte Clients nicht laden'
         })
     });
 }

 /**
  * Dialog öffnen: "Neuen Client erstellen"
  */
 showCreateDialog(): void {
   this.newClient = { clientApplicationId: '', displayName: '', systemId: null };
   this.displayCreateDialog = true;
 }

 /**
  * Erzeugt einen neuen Client. Erwartet im Backend:
  * POST /client-applications
  * Body = { clientId, displayName, systemId }
  */
 createClient(): void {
   const payload = {
    clientApplicationId:    this.newClient.clientApplicationId!,
     displayName: this.newClient.displayName!,
     systemId:    this.newClient.systemId
   };
   this.http
     .post<ClientApplicationSecretResponse>(this.authServerUrl + '/client-applications', payload)
     .subscribe({
       next: (res) => {
         this.messageService.add({
           severity: 'success',
           summary: 'Client Erstellt',
           detail: `ClientId: ${res.clientApplicationId} \n Secret: ${res.clientSecret}`,
           closable: true,
           life: 60000 // 10 Sekunden
         });
         this.displayCreateDialog = false;
         this.loadClients();
       },
       error: () =>
         this.messageService.add({
           severity: 'error',
           summary: 'Fehler',
           detail: 'Konnte Client nicht erstellen'
         })
     });
 }

 /**
  * Dialog öffnen: "Client bearbeiten"
  */
 showEditDialog(client: ClientApplication): void {
   this.editClient = { ...client };
   this.updatedDisplayName = client.displayName || '';
   this.updatedSystemId = client.systemId || 0;
   this.displayEditDialog = true;
 }

 /**
  * Aktualisiert nur den DisplayName des Clients.
  * Erwartet im Backend:
  * PUT /client-applications/{clientId}
  * Body = { displayName }
  */
 updateClient(): void {
   const payload = { displayName: this.updatedDisplayName!, systemId: this.updatedSystemId || 0 };
   this.http
     .put(this.authServerUrl + `/client-applications/${this.editClient.clientApplicationId}`, payload)
     .subscribe({
       next: () => {
         this.messageService.add({
           severity: 'success',
           summary: 'Aktualisiert',
           detail: 'Client erfolgreich aktualisiert'
         });
         this.displayEditDialog = false;
         this.loadClients();
       },
       error: () =>
         this.messageService.add({
           severity: 'error',
           summary: 'Fehler',
           detail: 'Konnte Client nicht aktualisieren'
         })
     });
 }

 /**
  * Löscht einen Client.
  * Erwartet im Backend:
  * DELETE /client-applications/{clientId}
  */
 deleteClient(client: ClientApplication): void {
   if (!confirm(`Client "${client.clientApplicationId}" wirklich löschen?`)) {
     return;
   }
   this.http
     .delete(this.authServerUrl + `/client-applications/${client.clientApplicationId}`)
     .subscribe({
       next: () => {
         this.messageService.add({
           severity: 'success',
           summary: 'Gelöscht',
           detail: 'Client erfolgreich gelöscht'
         });
         this.loadClients();
       },
       error: () =>
         this.messageService.add({
           severity: 'error',
           summary: 'Fehler',
           detail: 'Konnte Client nicht löschen'
         })
     });
 }

 /**
  * Setzt das Client-Secret zurück.
  * Erwartet im Backend:
  * POST /client-applications/{clientId}/secret
  * (Body kann leer gelassen werden.)
  * Antwort: { clientId, clientSecret }
  */
 resetSecret(client: ClientApplication): void {
   this.http
     .post<ClientApplicationSecretResponse>(this.authServerUrl + `/client-applications/${client.clientApplicationId}/reset-secret`, {})
     .subscribe({
       next: res => {
         this.messageService.add({
           severity: 'info',
           summary: 'Secret zurückgesetzt',
           detail: `Neues Secret: ${res.clientSecret}`,
           closable: true,
           life: 60000 

         });
       },
       error: () =>
         this.messageService.add({
           severity: 'error',
           summary: 'Fehler',
           detail: 'Konnte Secret nicht zurücksetzen'
         })
     });
 }
}
